import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { InterventionGroup } from "../../../../../../store/onboarding/cases/types";
import Table from "react-bootstrap/Table";
import { getFullName } from "../../../../../../utils/NamesUtils";
import { getGoalTitle } from "../../../../../utils";
import LoggedProgressRow from "./LoggedProgressRow";
import { Student } from "../../../../../../store/onboarding/types";
import { LoggedProgress, StudentGoalProgress } from "../../../../../../store/onboarding/meeting-module/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Collapse } from "react-bootstrap";
import { faCaretDown, faCaretRight, faChartLine, faCheckCircle, faList } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { hideInterventionPlanModal, sendLogProgressDataToGraph, showInterventionPlanModal } from "../../../../../../store/onboarding/cases/actions";
import { openGoalProgressModal } from "../../../../../../store/onboarding/meeting-module/actions";
import useInterventionGroupAccess from "../../../../../../utils/hooks/useInterventionGroupAccess";

type OwnProps = {
  interventionGroup: InterventionGroup;
  hideActions?: boolean;
  studentsFilter?: any[];
  isStudentDetail?: boolean;
  isGroupDetail?: boolean;
  selectedMeasurement?: any;
  showExitStudents?: boolean;
  fromInterventionFidelityChart?: boolean;
  expanded?: boolean;
};

type Props = OwnProps;

const LoggedProgressTable: FunctionComponent<Props> = (props) => {
  const { 
    interventionGroup, 
    hideActions, 
    studentsFilter, 
    selectedMeasurement, 
    isStudentDetail, 
    isGroupDetail,
    showExitStudents,
    fromInterventionFidelityChart,
    expanded
  } = props;
  const dispatch = useDispatch();
  const [openId, setOpenId] = useState<number | null>(null);
  const elementRef = useRef<(HTMLTableCellElement | null)[]>([]);

  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(interventionGroup.id);

  const showActions = useMemo(() => {
      return !hideActions 
          && !isStudentDetail 
          && !isGroupDetail 
          && !isIntGrpIDsWithReadAccess
    }, [hideActions, isStudentDetail, isGroupDetail, isIntGrpIDsWithReadAccess]);


  const toggleHandler = (id: number) => () => {
    setOpenId(openId => openId === id ? null : id);
  };


  const handleLogGoalProgress = useCallback(() => {
    dispatch(hideInterventionPlanModal());
    dispatch(
      openGoalProgressModal({
        onClose: () => dispatch(showInterventionPlanModal()),
      })
    );
  }, []);


  const logGoalProgressBtn = useMemo(() => {
    return (
      <button className="blueBtnSm " onClick={handleLogGoalProgress}>
        <FontAwesomeIcon
          icon={faChartLine}
          size={"lg"}
          style={{ color: "white", marginRight: "0.5rem" }}
        />{" "}
        log goal progress
      </button>
    );
  }, [handleLogGoalProgress]);

  let allActiveGoalsArray = useMemo(() => {
    let allGoals = new Set();
    let allActiveGoals = new Set();

    
    interventionGroup?.student_goals
    .filter((sg) => fromInterventionFidelityChart ? !sg.is_general_outcome : true)
    .map((goal, index) => {
      allGoals.add(goal.id);
      if (!goal.archived) {
        allActiveGoals.add(goal.id);
      }
    });
    return  Array.from(allActiveGoals);
  },[]);

  let allArchivedGoalsArray = useMemo(() => {
    let allGoals = new Set();
    let allArchivedGoals = new Set();
  
    interventionGroup?.student_goals
    .filter((sg) => fromInterventionFidelityChart ? !sg.is_general_outcome : true)
    .map((goal, index) => {
      allGoals.add(goal.id);
      if (goal.archived) {
        allArchivedGoals.add(goal.id);
      }
    });
    return  Array.from(allArchivedGoals);
  },[]);

  const filtered = useMemo(() => {
    let filterData: StudentGoalProgress[] = [];
    let modifiedData:any[] = [];
    if(fromInterventionFidelityChart) {
      let progressOverviewGoalProgress: StudentGoalProgress[] = 
      interventionGroup.progress_overview?.student_goal_progress
      .filter((element) =>!element.is_general_outcome && allActiveGoalsArray.includes(element.student_goal_id)
                ) || [];
      if(showExitStudents) {
          const archivedResult = interventionGroup.progress_overview?.student_goal_progress
          .filter((element) => !element.is_general_outcome && allArchivedGoalsArray.includes(element.student_goal_id)
        ) || [];
        progressOverviewGoalProgress = progressOverviewGoalProgress.concat(archivedResult)
      }

      if (selectedMeasurement?.value) {
        filterData =  (
          progressOverviewGoalProgress ?? []
        ).filter((sgp) => 
            sgp.measurement.id == +selectedMeasurement?.value
        ).sort((a, b) => a.is_primary_goal ? -1 : 1);
      }
      if (studentsFilter?.length) {
        filterData =  (
          progressOverviewGoalProgress ?? []
        ).filter((sgp) => studentsFilter?.some((sf) => +sf.value === sgp.student.id)
        ).sort((a, b) => a.is_primary_goal ? -1 : 1);
      } 
     if (studentsFilter?.length && selectedMeasurement?.value) {
        filterData =  (
          progressOverviewGoalProgress ?? []
        ).filter((sgp) => 
            ((sgp.measurement.id == +selectedMeasurement?.value) 
                && studentsFilter?.some((sf) => +sf.value === sgp.student.id)
                )
        ).sort((a, b) => a.is_primary_goal ? -1 : 1);
      } 
      if(!studentsFilter?.length && !selectedMeasurement?.value) {
        filterData = progressOverviewGoalProgress.sort((a, b) => a.is_primary_goal ? -1 : 1) ?? [];
      }
    } else {
      filterData = interventionGroup.progress_overview?.student_goal_progress.sort((a, b) => a.is_primary_goal ? -1 : 1) ?? [];
    }

    for (let index = 0; index < filterData.length; index++) {
      const element = filterData[index];
      if(filterData.some((fd) => fd.student.id == element.student.id)){
        modifiedData = {
          ...modifiedData,
          [element.student.id as number]: {
            ...modifiedData[element.student.id as number],
            [`${element.student_goal_id}_${element.is_primary_goal}`]: {
              ...element
            }
          }
        }
      }
    }
    dispatch(sendLogProgressDataToGraph(filterData));
    return modifiedData;
  }, [
    interventionGroup.progress_overview?.student_goal_progress,
    studentsFilter,
    selectedMeasurement,
    showExitStudents,
  ]);

  const getModalBody = () => {
    if(Object.keys(filtered).length) {
      let studentWithGoalProgressArray: any[] = [];
      let index:number = 0;
      for (const studentId in filtered) {
        if (Object.prototype.hasOwnProperty.call(filtered, studentId)) {
          let studentWithGoalProgress: any;
          const sgp = filtered[studentId];
          let student: any;
          let loggedProgressCount:number = 0
          let loggedProgressRow: any[] = [];
          for (const innerKey in sgp) {
            const sgpObj: StudentGoalProgress = sgp[innerKey];
            let loggedProgressData;
            if (Object.prototype.hasOwnProperty.call(sgp, innerKey)) {
              let studentGoals = interventionGroup.student_goals?.filter(
                (sg) => sg.id == sgpObj.student_goal_id
                );
              const latestStudentGoal = studentGoals.sort(
                (a, b) => Date.parse(b.created) - Date.parse(a.created)
                );
              student = sgpObj.student;
              loggedProgressData =  
                  (<tr className="tr-border">
                    <td className="" 
                        style={{
                          //minHeight: sgpObj.logged_progress.length > 1  ? '96px' : '71px', 
                          minWidth: '41px', 
                          borderRight:'1px solid #ccc'
                        }}
                  >
                    { sgpObj.is_primary_goal ? 
                                  <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    size="1x"
                                    className="blueText"
                                    title="Primary Goal"
                                  />
                                : ''
                              }
                    </td>
                    <td className="py-1 td-border-t" 
                        style={{
                          //minHeight: sgpObj.logged_progress.length > 1 ? '96px' : '71px', 
                          width: expanded ? "680px" : "",
                          borderRight: openId === sgpObj.student_goal_id ? '1px solid #ccc' : 'none'
                        }}>
                      <div
                        className="d-flex align-items-center"
                        aria-controls={`common-collapse-${sgpObj.student_goal_id}`}
                        aria-expanded={openId === sgpObj.student_goal_id} 
                      >
                        <div className="mr-3" role="button" onClick={toggleHandler(sgpObj.student_goal_id)}>
                          <FontAwesomeIcon
                            size={"1x"}
                            icon={
                              openId === sgpObj.student_goal_id
                                ? faCaretDown
                                : openId !== sgpObj.student_goal_id ? faCaretRight : faCaretDown
                            }
                          />
                        </div>
                      {latestStudentGoal && getGoalTitle(latestStudentGoal[0])}
                      </div>
                    </td>
                    <td colSpan={3} role="button" style={{width: expanded ? "318px" : "165px"}} className="p-0 td-border-t">
                      <Table style={{marginBottom:0}} className="mb-0 secondryGoalTable">
                        <tbody>
                          <Collapse in={openId === sgpObj.student_goal_id}>
                            <div id={`common-collapse-${sgpObj.student_goal_id}`} className="default-pointer">
                              {sgpObj.logged_progress.map((lp:LoggedProgress, innerIndex:number) => (
                                <LoggedProgressRow
                                  key={`lp_${lp.id}`}
                                  lp={lp}
                                  sgp={sgpObj}
                                  interventionGroup={interventionGroup}
                                  hideActions={hideActions}
                                  isStudentDetail={isStudentDetail}
                                  isGroupDetail={isGroupDetail}
                                  innerIndex={innerIndex}
                                  fromInterventionFidelityChart={fromInterventionFidelityChart}
                                  expanded={expanded}
                                />
                              ))}
                            </div>
                          </Collapse>             
                        </tbody>
                      </Table>
                    </td>
                  </tr>)
              loggedProgressCount++;
              loggedProgressRow.push(loggedProgressData)
            }
          }
          index++;
          studentWithGoalProgress = 
              <tr key={index}>
                <td ref={ref => {
                      elementRef.current[index] = ref
                    }} 
                style={{borderTop: index == 0 ? '0' : '1'}}>
                  {getFullName(student)}
                </td>
                <td colSpan={3} className="p-0 border-0">
                  <Table style={{marginBottom:0, marginTop: expanded ? "-1px" : "0px"}} className="mb-0 secondryGoalTable">
                    <tbody>
                      {loggedProgressRow}
                    </tbody>
                  </Table>
                </td>
                {!loggedProgressCount && <td colSpan={3} />}
              </tr>;
         
          studentWithGoalProgressArray.push(studentWithGoalProgress);
        }
      }
      return studentWithGoalProgressArray;

    } else {
      return (
        <>
            <tr>
              <td colSpan={5} className="text-center p-3">No Goal Progress Logged.</td>
            </tr>
            {!isStudentDetail && !isGroupDetail && !isIntGrpIDsWithReadAccess ?
            <tr style={{ backgroundColor:'#fff'}}>
              <td colSpan={6} className="text-center border-top-0 pt-1">{logGoalProgressBtn}</td> 
            </tr> : ''}
        </>
      )
    }
  }

  const getModalTable = () => {
    if(fromInterventionFidelityChart) {

      if(Object.keys(filtered).length) {
        return (
          <Table className="goalTable mb-0 secondryGoalTable table logged-progress-table">
            <thead>
              <tr>
                <th  style={{width: expanded ? "15%" : "175px"}}>Student</th>
                <th  style={{width: expanded ? "10px" : "65px"}} className="text-left"></th>
                <th style={{width: expanded ? showActions ? "56%" : "55%" : "110px"}} className="text-left">Goal</th>
                <th className="text-left" colSpan={3} style={{width: expanded ? showActions ? "23%" : "24%" : "214px"}}>{openId ? 'Progress' : ''}</th>
              </tr>
            </thead>
            <tbody>
              {getModalBody()}
            </tbody>
          </Table>
        )
      } else {
        return null;
      }
    } else {
      return (
        <Table className="goalTable mb-0 secondryGoalTable logged-progress-table">
          <thead>
              <tr>
                <th  style={{width: expanded ? "15%" : "15%"}}>Student</th>
                <th  style={{width: expanded ? "10px" : "65px"}} className="text-left"></th>
                <th style={{width: expanded ? showActions ? "56%" : "55%" : showActions ? "29%" : "38%"}} className="text-left">Goal</th>
                <th className="text-left" colSpan={3} style={{width: expanded ? showActions ? "23%" : "24%" : "214px"}}>{openId ? 'Date & Score' : ''}</th>
              </tr>
          </thead>
          <tbody>
            {getModalBody()}
          </tbody>
        </Table>
      )
    }
  }
  return (getModalTable());
};

export default LoggedProgressTable;
